import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import domestic900 from './../../assets/images/tenancy/tenancy06.jpg';
import domestic720 from './../../assets/images/tenancy/tenancy02.jpg';
import domestic500 from './../../assets/images/tenancy/tenancy03.jpg';
import domestic1200 from './../../assets/images/tenancy/tenancy04.jpg';
import commercial900 from './../../assets/images/tenancy/tenancy05.jpg';
import commercial720 from './../../assets/images/tenancy/tenancy01.jpg';
import commercial500 from './../../assets/images/tenancy/tenancy07.jpg';
import commercial1200 from './../../assets/images/tenancy/tenancy08.jpg';
import carpet900 from './../../assets/images/tenancy/tenancy14.jpg';
import carpet720 from './../../assets/images/tenancy/tenancy13.jpg';
import carpet500 from './../../assets/images/tenancy/tenancy11.jpg';
import carpet1200 from './../../assets/images/tenancy/tenancy10.jpg';
import window900 from './../../assets/images/tenancy/tenancy12.jpg';
import window700 from './../../assets/images/tenancy/tenancy09.jpg';
import window500 from './../../assets/images/tenancy/tenancy15.jpg';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What does end of tenancy cleaning include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our end of tenancy cleaning includes kitchen degreasing, appliance cleaning, bathroom sanitising, limescale removal, dusting, skirting boards, internal windows, doors, and high-touch surfaces to help leave the property in handover-ready condition.'
      }
    },
    {
      '@type': 'Question',
      name: 'Do you cover Tunbridge Wells and nearby areas?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We provide end of tenancy cleaning in Tunbridge Wells and surrounding Kent areas, including nearby villages and residential postcodes.'
      }
    },
    {
      '@type': 'Question',
      name: 'Can landlords and letting agents book directly?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely. We work with tenants, landlords and letting agents, and can tailor appointments around check-out and key handover schedules.'
      }
    },
    {
      '@type': 'Question',
      name: 'How far in advance should I book a move-out clean?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We recommend booking as early as possible, especially at month-end and weekends, but we also do our best to support short-notice end of tenancy bookings.'
      }
    }
  ]
};

const galleryImages = [
  { src: domestic900, alt: 'End of tenancy cleaned kitchen in Tunbridge Wells', caption: 'Kitchen deep clean finish' },
  { src: domestic720, alt: 'Move-out cleaning result in living room', caption: 'Living area refreshed' },
  { src: domestic500, alt: 'Domestic tenancy handover cleaning detail', caption: 'Dust and surface detailing' },
  { src: domestic1200, alt: 'Property handover standard cleaning', caption: 'Handover-ready presentation' },
  { src: commercial900, alt: 'Professional cleaner preparing tenancy property', caption: 'Professional cleaning team' },
  { src: commercial720, alt: 'End of tenancy room cleaning in progress', caption: 'Room-by-room deep clean' },
  { src: commercial500, alt: 'Letting agent cleaning support service', caption: 'Agent-ready standards' },
  { src: commercial1200, alt: 'Landlord property refresh cleaning result', caption: 'Landlord-ready finish' },
  { src: carpet900, alt: 'Carpet cleaning during end of tenancy service', caption: 'Carpet refresh treatment' },
  { src: carpet720, alt: 'Before-and-after style tenancy carpet clean', caption: 'Stain and odour reduction' },
  { src: carpet500, alt: 'Deep carpet clean in move-out property', caption: 'Deep fibre cleaning' },
  { src: carpet1200, alt: 'Tenancy carpet detail cleaning close-up', caption: 'Finishing touches' },
  { src: window900, alt: 'Window and glass cleaning for tenancy handover', caption: 'Glass and frame cleaning' },
  { src: window700, alt: 'End of tenancy cleaner working in property', caption: 'Reliable local service' },
  { src: window500, alt: 'Final inspection ready end of tenancy clean', caption: 'Final handover quality' }
];

export const EndOfTenancyPage = () => {
  useEffect(() => {
    document.title = 'End of Tenancy Cleaning Tunbridge Wells | Mr and Mrs Cleaning';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Book professional end of tenancy cleaning in Tunbridge Wells and nearby Kent areas. Trusted by tenants, landlords and letting agents.'
      );
    }
  }, []);

  return (
    <main className="tenancy-page">
      <section className="tenancy-hero">
        <p className="tenancy-kicker">Local Specialist Service</p>
        <h1>End of Tenancy Cleaning in Tunbridge Wells</h1>
        <p>
          Mr and Mrs Cleaning provides thorough move-out and end of tenancy cleaning for tenants,
          landlords and letting agents in Tunbridge Wells and surrounding areas. We focus on the
          details that matter at handover, from deep-cleaning kitchens to sanitising bathrooms and
          refreshing living spaces.
        </p>
        <p>
          Our team is trusted for reliable attendance, clear communication and quality results that
          help properties look their best before inventory checks and new tenant move-ins.
        </p>
      </section>

      <section className="tenancy-longform">
        <h2>Why choose our tenancy cleaning service?</h2>
        <p>
          End of tenancy standards are often higher than routine domestic cleaning. Our process is
          designed around that expectation, with practical cleaning checklists, consistent room-by-room
          coverage and close attention to visible build-up such as grease, limescale, dust and marks on
          surfaces.
        </p>
        <p>
          We regularly clean properties in Tunbridge Wells and nearby Kent locations, so we understand
          the needs of local tenants preparing to check out and local landlords preparing properties for
          viewings or incoming residents.
        </p>

        <h3>Typical end of tenancy cleaning tasks</h3>
        <ul>
          <li>Kitchen deep clean including exterior appliance detailing and cupboard wipe-downs.</li>
          <li>Bathroom sanitising, descaling and fixture polishing.</li>
          <li>Internal doors, skirting boards, frames and high-touch point cleaning.</li>
          <li>Dust and debris removal from bedrooms, living rooms, hallways and stairs.</li>
          <li>Floor finishing and presentation-focused final checks before handover.</li>
        </ul>

        <h3>Areas we serve</h3>
        <p>
          We cover Tunbridge Wells and surrounding areas in Kent. If your property is nearby and you
          need a move-out clean, contact us and we will confirm availability and scheduling options.
        </p>
      </section>

      <section className="tenancy-gallery-content">
        <h2>End of Tenancy Cleaning Gallery</h2>
        <p>
          A selection of recent cleaning results from tenancy and move-out jobs completed by our local
          team in Tunbridge Wells and nearby Kent areas.
        </p>
        <div className="tenancy-gallery-grid">
          {galleryImages.map((image, index) => (
            <figure className="tenancy-gallery-item" key={index}>
              <img src={image.src} alt={image.alt} loading="lazy" />
              <figcaption>{image.caption}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="tenancy-faq-content">
        <h2>End of Tenancy Cleaning FAQs</h2>
        <article>
          <h3>What does end of tenancy cleaning include?</h3>
          <p>
            It includes a full deep clean of key rooms and surfaces, focusing on kitchen and bathroom
            standards, dust removal, and detail cleaning for a handover-ready presentation.
          </p>
        </article>
        <article>
          <h3>Do you offer local end of tenancy cleaning around Tunbridge Wells?</h3>
          <p>
            Yes. We cover Tunbridge Wells and surrounding Kent areas for tenants, landlords and letting
            agents.
          </p>
        </article>
        <article>
          <h3>Can I book if my check-out date is close?</h3>
          <p>
            Yes, subject to availability. Contact us as soon as possible and we will do our best to fit
            your move-out timeline.
          </p>
        </article>
      </section>

      <section className="tenancy-cta">
        <h2>Book your end of tenancy clean</h2>
        <p>
          Call 07538 760864 or use our contact section to arrange a professional end of tenancy clean.
        </p>
        <div className="tenancy-cta-links">
          <a href="tel:07538760864">Call now</a>
          <Link to="/">Back to homepage</Link>
          <a href="/#contact">Go to contact form</a>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </main>
  );
};

export default EndOfTenancyPage;
